import type { QueryClient } from "@tanstack/react-query"

import {
  useSiteMinimalQuery,
  useRevampSiteQuery,
  useSiteResultsQuery,
} from "@/src/graphql/public-generated-rq"

// RevampSite has been split into several queries that surface (subsets of) the same
// Site: the full RevampSite plus the lighter SiteMinimal and SiteResults. A
// mutation that changes site data must refresh every variant that's mounted, so
// invalidate them together. Keep this list complete when adding a new
// RevampSite-derived query.
//
// Each base query generates four query-key prefixes (regular, Suspense, .infinite,
// .infiniteSuspense) that don't all equal `.rootKey` (the Suspense variants' rootKey is
// the base name, not the "Suspense"-suffixed key they actually query under), so build
// the full set of prefixes explicitly rather than matching on rootKey alone.
const REVAMP_SITE_ROOT_KEYS = new Set(
  [
    useRevampSiteQuery.rootKey,
    useSiteMinimalQuery.rootKey,
    useSiteResultsQuery.rootKey,
  ].flatMap((base) => [
    base,
    `${base}Suspense`,
    `${base}.infinite`,
    `${base}.infiniteSuspense`,
  ]),
)

const matchesRevampSite = (queryKey: readonly unknown[]) =>
  REVAMP_SITE_ROOT_KEYS.has(queryKey[0] as string)

export const invalidateRevampSite = (
  queryClient: QueryClient,
  opts?: { type?: "active" },
) =>
  queryClient.invalidateQueries({
    ...opts,
    predicate: (q) => matchesRevampSite(q.queryKey),
  })

export const refetchRevampSite = (
  queryClient: QueryClient,
  opts?: { type?: "active" },
) =>
  queryClient.refetchQueries({
    ...opts,
    predicate: (q) => matchesRevampSite(q.queryKey),
  })

const SITE_MINIMAL_STALE_TIME_MS = 24 * 60 * 60 * 1000

// SiteMinimal backs every page under a property's sidebar (General info, Electricity,
// Documents, Solar + battery, EV chargers), each mounting it independently. The app's
// global staleTime (5s) is too short to survive normal browsing, so it refetches on
// every page switch even though nothing changed. Every path that actually changes this
// data already goes through invalidateRevampSite/refetchRevampSite above, so a day-long
// staleTime is safe — it's just a backstop against a missed invalidation, not the
// mechanism keeping this data correct.
export const configureSiteMinimalQueryDefaults = (queryClient: QueryClient) =>
  queryClient.setQueryDefaults([useSiteMinimalQuery.rootKey], {
    staleTime: SITE_MINIMAL_STALE_TIME_MS,
  })
